import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export interface Job {
  id: string;
  post_id: number;
  preset: string;
  status: "queued" | "downloading" | "transcoding" | "done" | "failed";
  progress: number;
  error_msg: string | null;
  output_path: string | null;
  sidecar_path: string | null;
  requested_at: string;
  started_at: string | null;
  finished_at: string | null;
}

async function enqueueDownload(post_id: number, preset: string): Promise<Job> {
  const res = await fetch("/api/v1/downloads", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ post_id, preset }),
  });
  if (!res.ok) throw new Error(`Download failed: ${res.status}`);
  return res.json();
}

async function fetchDownloads(): Promise<Job[]> {
  const res = await fetch("/api/v1/downloads?limit=50");
  if (!res.ok) throw new Error("Failed to fetch downloads");
  const data = await res.json();
  return data.jobs ?? data;
}

export function useDownloads(pollInterval = 2000) {
  return useQuery({
    queryKey: ["downloads"],
    queryFn: fetchDownloads,
    refetchInterval: pollInterval,
    staleTime: 0,
  });
}

export function useEnqueue() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ post_id, preset }: { post_id: number; preset: string }) =>
      enqueueDownload(post_id, preset),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["downloads"] }),
  });
}

export function downloadUrl(jobId: string): string {
  return `/api/v1/downloads/${jobId}/file`;
}
